package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.ActiveDigicode
import fr.manyverse.app.domain.model.DigicodeStatus
import fr.manyverse.app.domain.repository.DigicodeRepository
import fr.manyverse.app.domain.usecase.CancelDigicodeUseCase
import fr.manyverse.app.domain.usecase.CloseDigicodeUseCase
import fr.manyverse.app.domain.usecase.CreateDigicodeUseCase
import fr.manyverse.app.domain.usecase.SubmitDigicodeAttemptUseCase
import fr.manyverse.app.domain.usecase.TimeoutDigicodeUseCase
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class DigicodeViewModel(
    private val createDigicodeUseCase: CreateDigicodeUseCase,
    private val submitDigicodeAttemptUseCase: SubmitDigicodeAttemptUseCase,
    private val cancelDigicodeUseCase: CancelDigicodeUseCase,
    private val timeoutDigicodeUseCase: TimeoutDigicodeUseCase,
    private val closeDigicodeUseCase: CloseDigicodeUseCase,
    private val digicodeRepository: DigicodeRepository,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    // GM state: creation and monitoring multiple active digicodes
    private val _launchState = MutableStateFlow<AsyncUiState<ActiveDigicode>>(AsyncUiState.Idle)
    val launchState: StateFlow<AsyncUiState<ActiveDigicode>> = _launchState.asStateFlow()

    private val _gmActiveDigicodes = MutableStateFlow<List<ActiveDigicode>>(emptyList())
    val gmActiveDigicodes: StateFlow<List<ActiveDigicode>> = _gmActiveDigicodes.asStateFlow()

    private val gmObservationJobs = mutableMapOf<String, Job>()

    // Player state: single active digicode + minimize
    private val _activeDigicode = MutableStateFlow<ActiveDigicode?>(null)
    val activeDigicode: StateFlow<ActiveDigicode?> = _activeDigicode.asStateFlow()

    private val _isMinimized = MutableStateFlow(false)
    val isMinimized: StateFlow<Boolean> = _isMinimized.asStateFlow()

    private val _submitState = MutableStateFlow<AsyncUiState<ActiveDigicode>>(AsyncUiState.Idle)
    val submitState: StateFlow<AsyncUiState<ActiveDigicode>> = _submitState.asStateFlow()

    private var observePlayerJob: Job? = null
    private var observingTableId: String? = null
    private var observingPlayerId: String? = null

    fun create(
        tableId: String,
        targetParticipantId: String,
        digitCount: Int,
        maxAttempts: Int,
        timeLimitSeconds: Int?,
        showAttemptsToPlayer: Boolean,
        soundEnabled: Boolean,
        solution: String,
        createdBy: String
    ) {
        launchAsyncWithCallback(
            state = _launchState,
            block = {
                createDigicodeUseCase(
                    tableId = tableId,
                    targetParticipantId = targetParticipantId,
                    digitCount = digitCount,
                    maxAttempts = maxAttempts,
                    timeLimitSeconds = timeLimitSeconds,
                    showAttemptsToPlayer = showAttemptsToPlayer,
                    soundEnabled = soundEnabled,
                    solution = solution,
                    createdBy = createdBy
                )
            },
            onSuccess = { digicode ->
                _gmActiveDigicodes.update { it + digicode }
                startObservingSingleDigicode(digicode.id)
            }
        )
    }

    fun submit(digicode: ActiveDigicode, attemptValue: String) {
        launchAsyncWithCallback(
            state = _submitState,
            block = {
                submitDigicodeAttemptUseCase(digicode = digicode, attemptValue = attemptValue)
            },
            onSuccess = { updatedDigicode ->
                _activeDigicode.value = updatedDigicode
            }
        )
    }

    fun cancel(digicodeId: String) {
        val digicode = _gmActiveDigicodes.value.find { it.id == digicodeId }
        closeGm(digicodeId)
        viewModelScope.launch {
            cancelDigicodeUseCase(digicodeId = digicodeId).onFailure { error ->
                // Le joueur a toujours le digicode : on le réaffiche pour que le MJ puisse réessayer
                if (digicode != null && _gmActiveDigicodes.value.none { it.id == digicodeId }) {
                    _gmActiveDigicodes.update { it + digicode }
                    startObservingSingleDigicode(digicodeId)
                }
                errorNotifier.notify(error)
            }
        }
    }

    fun timeout(digicodeId: String) {
        viewModelScope.launch {
            timeoutDigicodeUseCase(digicodeId = digicodeId).onFailure(errorNotifier::notify)
        }
    }

    fun close() {
        closeDigicodeUseCase()
        _activeDigicode.value = null
        _isMinimized.value = false
    }

    fun minimize() {
        _isMinimized.value = true
    }

    fun restore() {
        _isMinimized.value = false
    }

    fun closeGm(digicodeId: String) {
        gmObservationJobs[digicodeId]?.cancel()
        gmObservationJobs.remove(digicodeId)
        _gmActiveDigicodes.update { it.filter { d -> d.id != digicodeId } }
    }

    /**
     * Fetches and starts observing all active digicodes for a table (GM).
     * Call on ON_RESUME to restore state after returning to the table.
     */
    fun startObservingForGm(tableId: String) {
        viewModelScope.launch {
            digicodeRepository.getActiveDigicodesForTable(tableId).onSuccess { digicodes ->
                val newIds = digicodes.map { it.id }.toSet()
                _gmActiveDigicodes.value = digicodes
                digicodes.forEach { if (it.id !in gmObservationJobs) startObservingSingleDigicode(it.id) }
                gmObservationJobs.keys.filter { it !in newIds }.forEach { closeGm(it) }
            }
        }
    }

    private fun startObservingSingleDigicode(digicodeId: String) {
        gmObservationJobs[digicodeId]?.cancel()
        gmObservationJobs[digicodeId] = viewModelScope.launch {
            digicodeRepository.observeDigicode(digicodeId).collect { digicode ->
                if (digicode != null) {
                    _gmActiveDigicodes.update { list ->
                        list.map { if (it.id == digicodeId) digicode else it }
                    }
                }
            }
        }
    }

    /**
     * Starts observing for a digicode targeting this player. Call once via LaunchedEffect(table.id).
     *
     * Pre-existing digicode (table just opened) → shown as a bubble.
     * New digicode arriving via Realtime → shown as fullscreen overlay.
     */
    fun startObservingForPlayer(tableId: String, playerId: String) {
        if (observePlayerJob?.isActive == true && observingTableId == tableId && observingPlayerId == playerId) {
            return
        }
        observePlayerJob?.cancel()
        observingTableId = tableId
        observingPlayerId = playerId
        observePlayerJob = viewModelScope.launch {
            val preExistingId = digicodeRepository.getActiveDigicodeForPlayer(tableId, playerId)
                .getOrNull()
                ?.also { existing ->
                    _activeDigicode.value = existing
                    _isMinimized.value = true
                }?.id

            digicodeRepository.observeActiveDigicodeForPlayer(tableId, playerId).collect { digicode ->
                when {
                    digicode == null || digicode.status == DigicodeStatus.CANCELLED -> {
                        _activeDigicode.value = null
                        _isMinimized.value = false
                    }
                    digicode.status != DigicodeStatus.ACTIVE && _isMinimized.value -> {
                        // Terminal state while player had the bubble → dismiss silently
                        _activeDigicode.value = null
                        _isMinimized.value = false
                    }
                    else -> {
                        val isNewDigicode = digicode.id != preExistingId && _activeDigicode.value?.id != digicode.id
                        if (isNewDigicode) _isMinimized.value = false
                        _activeDigicode.value = digicode
                    }
                }
            }
        }
    }

    /**
     * Re-synchronizes player digicode state at foreground return.
     * Shows as overlay if GM created a new digicode while player was in background.
     */
    fun refreshForPlayer(tableId: String, playerId: String) {
        viewModelScope.launch {
            digicodeRepository.getActiveDigicodeForPlayer(tableId, playerId).onSuccess { digicode ->
                if (digicode == null || _activeDigicode.value?.id == digicode.id) return@onSuccess
                _activeDigicode.value = digicode
                _isMinimized.value = false
            }
        }
    }

    fun resetLaunchState() {
        _launchState.value = AsyncUiState.Idle
    }

    override fun onCleared() {
        super.onCleared()
        observePlayerJob?.cancel()
        gmObservationJobs.values.forEach { it.cancel() }
        gmObservationJobs.clear()
    }
}
